import React, { useEffect, useRef } from "react";
import List, { ListItemText, ListItemGraphic } from "@material/react-list";
import MaterialIcon from "@material/react-material-icon";
import { Chevron } from "~/components/chevron";
import { AuthMenuItemProps } from "../types";
import { AuthMenuItem } from "../AuthMenuItem";
import _ from "lodash";
import styled from "@emotion/styled";
export * from "./SubmenuExpandCollapseProvider";
import { useSubmenuExpand } from "./SubmenuExpandCollapseProvider";

const useAutoCloseOnClick = (
  close: React.DispatchWithoutAction
): React.MutableRefObject<HTMLDivElement | null> => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const elem = ref.current;
    const clickHandler = () => close();

    elem.addEventListener("click", clickHandler);

    return () => elem.removeEventListener("click", clickHandler);
  }, [ref.current, close]);

  return ref;
};

export type SubmenuItemProps = AuthMenuItemProps & {
  shortTitle: string;
};

export type SubmenuProps = {
  title: string;
  icon: string;
  items: SubmenuItemProps[];
  unsorted?: true;
};

const CustomChevron = styled(Chevron)`
  margin-left: auto;
`;

const SubmenuContainer = styled("div")`
  overflow: hidden;
  transition: all 0.25s ease-in-out;
  padding: 0 0 0 56px !important;
  margin-top: -12px !important;

  & .mdc-list-item {
    height: 26px;
    font-size: 12px;
  }
`;

const CustomList = styled(List)`
  margin: 0;
  padding: 0;
`;

const itemHeight = 26;

export const Submenu: React.FC<SubmenuProps> = ({
  title,
  icon,
  items,
  unsorted,
}) => {
  const { collapse, expand, isExpanded } = useSubmenuExpand(title);
  const containerRef = useAutoCloseOnClick(() => collapse());

  const maxHeight = isExpanded ? (items.length + 1) * itemHeight : 0;

  const visibleItems = items.filter((x) => x.isVisible);

  if (visibleItems.length === 0) {
    return null;
  }

  if (visibleItems.length === 1) {
    return <AuthMenuItem {...visibleItems[0]} />;
  }

  const children = _.chain(visibleItems)
    .sortBy((x, idx) => (unsorted ? idx : x.shortTitle))
    .map((x) => (
      <AuthMenuItem
        {...x}
        title={x.shortTitle || x.title}
        key={x.shortTitle || x.title}
        compact
      />
    ))
    .value();

  const expandCollapseClick = () => {
    if (isExpanded) {
      collapse();
    } else {
      expand();
    }
  };

  return (
    <CustomList>
      <div onClick={() => expandCollapseClick()} className="mdc-list-item">
        <ListItemGraphic graphic={<MaterialIcon icon={icon} />} />
        <ListItemText primaryText={title} />
        <CustomChevron {...{ expanded: isExpanded }} />
      </div>
      <SubmenuContainer style={{ maxHeight }} ref={containerRef}>
        {children}
      </SubmenuContainer>
    </CustomList>
  );
};
